import React, { useMemo, useState } from 'react';

import { COMMON_ACTION } from './constants';
import type { TableColumns, CustomAction, TickActions } from './types';
import {
  useGetPaginatedApplicationViewTableData,
  type ApplicationViewTableDataItem,
} from './graphql';
import {
  getTableHeader,
  getQueryFilter,
} from './applicationViewTableFunctions';
import { ApplicationViewTableActionCell } from './ApplicationViewTableActionCell';

import {
  Alert,
  Loading,
  pageRowsSelectOptions,
  usePagination,
  Table,
  AdvancedPagination,
  type CheckBoxProps,
} from '../../../../components';
import { ApplicationViewTableTickActions } from './ApplicationViewTableTickActions';

export type ApplicationViewTableProps = {
  initialVisibleColumns?: TableColumns[];
  customActions?: CustomAction[];
  listFilter?: Record<string, any>;
  quickFilter?: Record<string, any>;
  tableFilter?: Record<string, any>;
  sort?: Record<string, any>;
  minWidth?: string;
  pickCommonActions?: COMMON_ACTION[];
  tickActions?: TickActions[];
};

export const ApplicationViewTable: React.FunctionComponent<
  ApplicationViewTableProps
> = (props: ApplicationViewTableProps) => {
  const {
    customActions,
    initialVisibleColumns,
    listFilter,
    quickFilter,
    tableFilter,
    minWidth,
    pickCommonActions = [
      COMMON_ACTION.MONITOR,
      COMMON_ACTION.VIEW_AS,
      COMMON_ACTION.ONE_OFF,
    ],
    tickActions = [],
    sort = {},
  } = props;

  const { pagination, setPage, setRowPerPage } = usePagination();
  const page = pagination?.page;
  const rowsPerPage = pagination?.rowsPerPage;
  const [checkedIndex, setCheckedIndex] = useState<number[]>([]); // to store all index of checked row(s)

  const queryFilter = getQueryFilter(tableFilter, listFilter, quickFilter);

  const {
    paginatedApplicationViewTableData,
    gsAssigneeOption,
    loading,
    error,
    refetch,
  } = useGetPaginatedApplicationViewTableData(queryFilter, sort, pagination);

  const pageCount = paginatedApplicationViewTableData?.totalPage;
  const recordsCount = paginatedApplicationViewTableData?.count;
  const visibleData = paginatedApplicationViewTableData?.data;

  const tickedApplicationIds = checkedIndex.map(rowIndex => {
    const { applicationId } = visibleData[rowIndex];
    return applicationId;
  });

  const onChangeCheckedIndex = (checked: number[]): void => {
    setCheckedIndex(checked);
  };

  const handleRequestSort = (
    property: keyof ApplicationViewTableDataItem,
  ): void => {
    // TODO: Get the sorted data from the server
  };

  const handlePageChange = async (
    event: unknown,
    newPage: number,
  ): Promise<void> => {
    await setPage(String(newPage));
  };

  const handleRowPerPageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await setRowPerPage(event.target.value);
    await setPage('1');
  };

  // TODO column selection
  const tableHeader = getTableHeader(gsAssigneeOption);
  const visibleColumns = initialVisibleColumns
    ? tableHeader.filter(header => {
        return initialVisibleColumns.includes(header?.id);
      })
    : tableHeader.filter(header => {
        return header?.initialVisible;
      });

  visibleColumns.push({
    id: 'action',
    label: 'Action',
    isSortable: false,
    initialVisible: true,
    render: row => {
      return (
        <ApplicationViewTableActionCell
          rowData={row}
          customActions={customActions}
          pickCommonActions={pickCommonActions}
          refetch={refetch}
        />
      );
    },
  });

  if (error) {
    return (
      <Alert
        openAlert
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const enableTickRows = !!tickActions.length;
  const checkBoxProps = useMemo<CheckBoxProps>(
    () => ({
      checkedIndex,
      onChangeCheckedIndex,
      checkedAll: checkedIndex.length === visibleData.length,
      onCheckAll: (checkAll: boolean) => {}, // TODO TO be removed in the future
    }),
    [checkedIndex, visibleData.length],
  );

  return loading ? (
    <Loading />
  ) : (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <div style={{ width: '100%', overflowX: 'auto', marginBottom: '10px' }}>
        {enableTickRows && (
          <ApplicationViewTableTickActions
            tickActions={tickActions}
            tickedApplicationIds={tickedApplicationIds}
          />
        )}
        <div style={{ width: '100%', minWidth: 'min-content' }}>
          <Table<ApplicationViewTableDataItem>
            height={'80vh'} // set a specific height to keep a sticky header
            headCells={visibleColumns}
            refetch={refetch}
            minWidth={minWidth}
            data={visibleData}
            showCheckBox={enableTickRows}
            checkBoxProps={checkBoxProps}
            handleRequestSort={handleRequestSort}
          />
        </div>
      </div>
      <AdvancedPagination
        page={page}
        count={pageCount}
        recordText={`${recordsCount} records`}
        rowsPerPageOptions={pageRowsSelectOptions}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={handleRowPerPageChange}
        onChange={handlePageChange}
      />
    </div>
  );
};
